import { isConnected, isWeb } from '../../../shared/utils/is';
import { hideLoadingDialog, confirmNotification, showLoadingDialog } from '../../../shared/utils/notifications';
import { EmptyListMessage } from './EmptyListMessage';
import { MobileSearchBar } from './MobileSearchBar';
import { BaseToolbarNavPageLayout } from './baseLayoutViews';

/**
 * A single selected item in a multiple select field
 *
 * @see MultiselectSelections
 * @see SelectFieldView
 */
export const MultiselectSelectionItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_select_field_selection_item',
    className: 'select_field_selection_item',
    model: null, // SelectFieldSelections.model

    events: {
        'click .remove': 'unselect_item'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    unselect_item: function (event) {
        this.model.trigger('destroy', this.model, this.model.collection); // Do not use model.destroy(), as that tries to make a server call
        return false;
    }
});

/**
 * A set of selected multiple choice options for the select field view.
 * @see SelectFieldView
 */
export const MultiselectSelections = Backbone.Marionette.CollectionView.extend({
    collection: null, // varies based on typeahead
    itemView: MultiselectSelectionItem,

    on_item_removed: function (collection) {}, // callback for when a model is removed
    on_item_clicked: function (event) {}, // what to do when an item is clicked

    events: {
        'click .select_field_selection_item:not(.remove)': 'on_item_clicked'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        var this_ = this;
        this.stopListening(this.collection, 'remove');
        this.listenTo(this.collection, 'remove', function () {
            this_.on_item_removed(this_.collection);
        });
    }
});

/**
 * This is mobile implementation of our typeahead. It creates the input box and behaviors around launching the modal.
 * If `allow_multiple` is true, this will also display the chosen options above the typeahead box.
 * NOT returning the results (there is no event trigger from the modal_class automatically updating the displayed value in the input box)
 * this should be extended by callers according to their input types and output preferences (account, user, template etc)
 * @see: BaseTypeaheadModal
 */
export const MobileBaseTypeahead = Backbone.Marionette.Layout.extend({
    template: '#tpl_zp_autocomplete',
    model: null,
    collection: null, // Collection to search on (used by typeahead modal)
    i18n_key_placeholder: '', // this is rendered via Marionette
    modal_class: null, // pass in extended BaseTypeaheadModal instance
    allow_multiple: false, // allow the user to select multiple results?
    collection_class: null, // the class for this.collection. Necessary for multi-select if get_current_selections() does not return a Collection
    html_name: null, // Optinal name for our HTML element. Callers may need this so Backbone.Validation can mark us as invalid
    is_create_context: false, // Whether or not this view is used to create a new task/project

    events: {
        'focus input': 'launch_modal'
    },

    regions: {
        r_selections: '.selected_list_container'
    },

    templateHelpers: function () {
        return {
            default_value: this.get_default_value(),
            allow_multiple: this.allow_multiple,
            placeholder: _t(this.i18n_key_placeholder) || this.i18n_key_placeholder,
            html_name: this.html_name
        };
    },

    // To pass in with modal options
    post_on_select: function (model) {},
    on_clear: function () {},

    /**
     * Subclasses must implement to get the default value (usually from the model). This needs
     * to be in a function in case the model changes and the default value has changed.
     */
    get_default_value: function () {
        throw new Error('Programmer error: BaseTypeahead.get_default_value() not implemented.');
    },

    /**
     * [Multiselect Only]
     * Get the currently selected items. This will be used to set `default_selections` for the typeahead modal.
     * @return {collection|array|json}
     */
    get_current_selections: function () {
        throw new Error('Programmer error: BaseTypeahead.get_current_selections() not implemented.');
    },

    /**
     * [Multiselect Only]
     * Optional callback for when an item is removed from the selections. This will NOT be called when
     * the selections are updated via the BaseTypeaheadModal. It's unnecessary to call render() in this,
     * as that's generally covered by MultiselectSelections.
     */
    on_item_removed: function (collection) {},

    /**
     * Get a the options that will be used to instantiate this.modal_class
     * @return {dictionary} the options that should be sent to the BaseTypeaheadModal for this class
     */
    get_modal_options: function () {
        var options = {
            model: this.model,
            post_on_select: this.post_on_select,
            on_clear: this.on_clear,
            default_value: this.get_default_value(),
            allow_multiple: this.allow_multiple,
            default_selections: this.allow_multiple ? this.get_current_selections() : null,
            is_create_context: this.is_create_context
        };

        // Passing in `null` or `undefined` may overwrite our modal's defaults, so we need to check first:
        if (this.collection) {
            options.collection = this.collection;
        }
        if (this.collection_class) {
            options.collection_class = this.collection_class;
        }
        if (this.allow_multiple) {
            options.default_selections = this.get_current_selections();
        }

        return options;
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.modal_class) {
            throw new Error('Programmer Error: must pass modal_class to MobileBaseTypeahead.');
        }

        if (this.allow_multiple) {
            this.i18n_key_placeholder = 'mobile_placeholder_select';
        }

        // for web, re-render when user has made a selection in the modal (or cleared)
        var _post_on_select = this.post_on_select;
        var this_ = this;
        this.post_on_select = function (model) {
            _post_on_select(model);
            this_.render(); // only needed for web - no effect for native
        };

        var _on_clear = this.on_clear;
        this.on_clear = function () {
            _on_clear();
            this_.render(); // only needed for web - no effect for native
        };
    },

    render: function () {
        MobileBaseTypeahead.__super__.render.apply(this, arguments);
        if (!this.allow_multiple) {
            return;
        }
        var this_ = this;

        // For multiple choice, show the selections/typeahead:
        var selections = this.get_current_selections();
        if (!(selections instanceof Backbone.Collection)) {
            if (!this.collection_class) {
                throw new Error(
                    'Programmer Error: collection_class not defined, and MobileBaseTypeahead.get_current_selections() not returning Collection.'
                );
            }

            selections = new this.collection_class(selections);
        }

        this.r_selections.show(
            new MultiselectSelections({
                collection: selections,
                on_item_removed: this.on_item_removed,
                on_item_clicked: function (event) {
                    this_.launch_modal(event);
                }
            })
        );
    },

    launch_modal: function (event) {
        if (!$(event.target).hasClass('fake_input')) {
            return;
        } // android 4.1 trigging multiple clicks at once >:(
        $f.mobile.nav.show_modal(this.modal_class, this.get_modal_options());
    }
});

/**
 * The list of results for a BaseTypeaheadModal.
 */
export const BaseTypeaheadResults = Backbone.Marionette.CollectionView.extend({
    tagName: 'ul',
    className: 'typeahead_modal_content pagelist results',
    collection: null, // passed in from BaseTypeaheadModal
    itemView: null, // passed in from BaseTypeaheadModal
    itemViewOptions: null, // passed in from BaseTypeaheadModal
    itemViewEventPrefix: 'item',

    triggers: {
        item_selected__noop: 'item_selected' // we receive = from our children views and re-trigger it up to our containing Layout
    },

    getEmptyView: function () {
        return EmptyListMessage.extend({
            i18n_key_message: 'mobile_empty_list_message_no_results_found'
        });
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        var this_ = this;
        this.on('item:select_toggled', function (view, model) {
            this_.trigger('item_selected', view, model);
        });

        this.listenTo(this.collection, 'sort', this.render);
    }
});

/**
 * A single result in a BaseTypeaheadResults.
 */
export const BaseTypeaheadItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_typeahead_item',
    tagName: 'li',
    show_checkmark: false,

    format_result: function (model) {}, // convert model to text that should be displayed - passed in from BaseTypeheadModal

    triggers: {
        selected__noop: 'select_toggled'
    },

    modelEvents: {
        change: 'render'
    },

    events: {
        click: 'on_clicked'
    },

    templateHelpers: function () {
        return {
            formatted_result: this.format_result(this.model),
            show_checkmark: this.show_checkmark,
            is_checked: this.model.is_checked
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    on_clicked: function (event) {
        if (this.show_checkmark) {
            this.toggle_checked();
        }

        this.trigger('select_toggled', this.model);
        return false;
    },

    toggle_checked: function () {
        if (this.model.is_checked) {
            this.model.is_checked = false;
            this.render();
        } else {
            this.model.is_checked = true;
            this.render();
        }
    }
});

/**
 * A single result in a BaseTypeaheadResults that contains html.
 */
export const BaseTypeaheadHTMLItem = BaseTypeaheadItem.extend({
    template: '#tpl_typeahead_html_item'
});

/**
 * Our base typeahead modal, can be launched from the basetypeahead or from other code using .show_modal(class_init())
 * @see MobileBaseTypeahead
 */
export const BaseTypeaheadModal = BaseToolbarNavPageLayout.extend({
    template: '#tpl_typeahead_modal',
    is_modal: true,

    // Used by both multi-select and single-select:
    collection: null, // a collection of all results that will be used to search
    toolbarnav_title: '',
    default_value: '',
    default_search: '', // default is search by all, i.e. empty string
    allow_multiple: false,
    focus_search_on_open: true, // focus on the search bar (opening the keyboard) when page loads?
    toolbarnav_center_title: true,

    // Used by multi-select only:
    collection_class: null, // What class is this.collection? Needed for cloning, etc.
    default_selections: null, // What models should be selected by default (i.e., which were previously selected)? Could be backbone model, collection, or JSON
    _default_selections_json: null, // Our internal JSON clonse of default_selections
    _selected_models: null, // We'll keep track of what the user has selected here. This is ultimately what will be provided in post_on_select();

    // These are passed to our r_results region
    itemView: BaseTypeaheadItem,
    itemViewOptions: function () {
        return {
            format_result: this.format_result,
            show_checkmark: this.allow_multiple
        };
    },

    regions: {
        r_results: '.typeahead_results_region'
    },

    events: {
        'click .multiselect_done': 'click_multiselect_done' // optional implementation
    },

    templateHelpers: function () {
        return {
            placeholder: this.placeholder || _t('mobile_placeholder_search_bar'),
            display_name: this.default_value,
            allow_multiple: this.allow_multiple
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.collection && !this.collection_class) {
            throw new Error('Programmer Error: must pass collection to BaseTypeaheadModal.');
        }

        // If we have a collection_class, we can generate a collection automatically using it.
        if (this.collection_class && !this.collection) {
            this.collection = new this.collection_class();
        }

        this.addRegion('r_search_bar', '.search_bar');

        if (!this.allow_multiple) {
            return;
        }

        if (!this.collection_class) {
            // collection_class is required by multiselect
            throw new Error('Programmer Error: must pass collection_class to BaseTypeaheadModal.');
        }

        // For multi-select, generate _default_selections_json from default_selections
        if (this.default_selections) {
            var json = null;
            if (this.default_selections instanceof Backbone.Collection) {
                json = this.default_selections.toJSON();
            } else {
                json = this.default_selections;
            }

            this._default_selections_json = $f.utils.deepclone(json); // json --> string --> json to remove pass-by-reference
        } else {
            this._default_selections_json = [];
        }

        this._selected_models = new this.collection_class(this._default_selections_json);

        if (this.collection.length > 0) {
            this.register_selected_results();
        }
    },

    render: function () {
        BaseTypeaheadModal.__super__.render.apply(this, arguments);
        var this_ = this;

        this.r_search_bar.show(
            new MobileSearchBar({
                collection: this.collection,
                placeholder: this.placeholder,
                default_value: this.default_value,
                on_search_clear: function (query) {
                    this_.on_clear();
                },
                search: function (query, callback) {
                    this_.search(query, callback);
                }
            })
        );

        this.r_results.show(
            new BaseTypeaheadResults({
                collection: this.collection,
                itemView: this.itemView,
                itemViewOptions: this.itemViewOptions(),
                getEmptyView: this.getEmptyView
            })
        );

        // Listen to the region and call our on_select() when the user selects something
        this.listenTo(this.r_results.currentView, 'item_selected', function (view, model) {
            if (!this_.allow_multiple || model.is_checked) {
                this_.on_select(model);
            } else {
                this_.on_deselect(model);
            }
        });
    },

    /**
     * Provide initial show functionality
     * Subclasses can implement prefetch local storage logic after calling super()
     */
    onShow: function () {
        BaseTypeaheadModal.__super__.onShow.apply(this, arguments);
        $f.application.vent.trigger('mobile:close_leftmenu');
        var this_ = this;

        if (this.focus_search_on_open) {
            // we need a slight delay before we can focus and hopefully open
            // the keyboard, depending on the mobile version
            setTimeout(function () {
                this_.$el.find('input.search').focus();
            }, 100);
        }

        // Only search on value changes and on fresh views
        var search_query = this.collection.paginator && this.collection.paginator.get('search_query');
        if (search_query && this.default_value !== search_query) {
            this.search(this.default_value);
        } else if (!this.is_historic_view) {
            this.search(this.default_search);
        }
    },

    /**
     * Called when an item is selected from the results
     *
     * @note: For typeahead modals with allow_multiple=true, subviews must implement their
     * own on_select() to handle those changes.
     */
    on_select: function (model) {
        if (this.allow_multiple) {
            this._selected_models.add(model);
            return false;
        }

        this.post_on_select(model);
        this.close();
        return false;
    },

    /**
     * Called when an item is deselected from the results. For allow_multiple=true only.
     */
    on_deselect: function (model) {
        if (!this.allow_multiple) {
            console.warn('on_deselect() called for single select typeahead.');
            return false;
        }

        this._selected_models.remove(model);
        return false;
    },

    /**
     * actual searching logic and rendering of the search results
     * @param {string} query Passed in from search_keypress
     */
    search: function (query, callback) {
        callback || (callback = function () {});
        if (!query) {
            query = '';
        }
        var this_ = this;

        var search_online = isWeb() || isConnected(); // @note this is ignored by our cache 2.0

        showLoadingDialog();
        this.collection.typeahead_search(
            query,
            function (results) {
                hideLoadingDialog();

                var _results = this_.transform_collection(results);

                if (_results !== results) {
                    // don't want to reset results unless we have to, as it will cause a second render of results list
                    if (_results instanceof Backbone.Collection) {
                        _results = _results.toJSON();
                    }
                    this_.collection.reset(_results);
                }

                if (this_.allow_multiple) {
                    this_.register_selected_results();
                }
                callback();
            },
            search_online
        );
    },

    /**
     * Multiple Select Only:
     * Loop through this.collection and set `model.is_checked = true` on each model that has been
     * previously selected.
     */
    register_selected_results: function () {
        var this_ = this;
        this.collection.each(function (model) {
            if (this_._selected_models.get(model.get(model.idAttribute))) {
                model.is_checked = true;
                model.trigger('change');
            } else {
                model.is_checked = false;
                model.trigger('change');
            }
        });
    },

    /**
     * Subclass can optionally implement logic to transform or filter the results returned from search
     * @param {Collection} results
     * @returns either a Collection or []/JSON
     */
    transform_collection: function (results) {
        return results;
    },

    /**
     * Called after the user selects a choice. Subclasses should implement this to create custom
     * logic (usually events, triggers or actions) after the selection is made
     * @param {Model} item Received from on_select, item from the collection that is selected
     */
    post_on_select: function (model) {},

    /**
     * Optional subclass function to be fired when the the user clears their search
     */
    on_clear: function () {
        return false;
    },

    /**
     * **************************************************************************************************
     * Implement the following functions in extended subclasses, throw errors if any are called without extension
     * **************************************************************************************************
     */

    /**
     * By default, will try to use item.get('title'). Otherwise, subclasses should implement this to format
     * the output of each row of the search results, it is added to the interior of base_item_render
     */
    format_result: function (item) {
        if (item.get('title')) {
            return item.get('title');
        }

        throw new Error(
            "Programmer Error: BaseTypeaheadModal subclass must implement format_result. Default 'title' not found.",
            {
                item_id: item.id,
                model: item.toJSON()
            }
        );
    },

    /**
     * For multiselect only: check if the user's selections have changed. This is used when clicking the
     * back button to leave the modal. If returns true, the user will be asked if they would like to save or discard their changes.
     * @return {boolean} true if selections have changed, false if not.
     */
    values_have_changed: function () {
        return JSON.stringify(this._selected_models.toJSON()) !== JSON.stringify(this._default_selections_json);
    },

    /**
     * For typeaheads that allow multiple selections, this will fire when the user clicks the done button. Alternatively,
     * this may be called programattically, in which case `event` will be a boolean telling us if we should save our results.
     */
    click_multiselect_done: function (event) {
        // If this is called programmatically, event is really a boolean telling us if we should save.
        // If event is really an event, default to true for save. Otherwise, take the passed in value.
        var save = event instanceof $.Event ? true : event;

        if (save) {
            this.post_on_select(this._selected_models);
        }
        this.close();

        return false;
    },

    /**
     * If `allow_multiple`, check to see if the user wants to keep their changes. Reverts if necessary, then calls close().
     */
    go_back: function () {
        if (!this.allow_multiple || !this.values_have_changed()) {
            BaseTypeaheadModal.__super__.go_back.apply(this, arguments);
            return;
        }

        var this_ = this;
        confirmNotification(
            _t('mobile_message_typehead_confirm_save_message'),
            function (answer) {
                if (answer) {
                    this_.click_multiselect_done(true);
                } else {
                    this_.click_multiselect_done(false);
                }
            },
            _t('mobile_message_typehead_confirm_save_title'),
            _t('mobile_common_button_yes'),
            _t('mobile_common_button_no')
        );

        return false;
    }
});
